import { IEgdApiClient, SettingType, IAdapterCollection, IAuth } from 'v2/modules/lib'

export interface IAccountSettingsFormArgs {
  egdApiClient: IEgdApiClient
  accountId: string
  adapters: IAdapterCollection
  auth: IAuth
}

export const AccountSettingsForm = ({ egdApiClient, accountId, adapters, auth }: IAccountSettingsFormArgs) => {
  const emptyQnsSettings: Record<string, any> = {
    allow_override: null,
    allow_whitelist: null,
    custom_inbound_schedule: null,
    custom_outbound_schedule: null,
    inbound_schedule: null,
    modified: 0,
    outbound_email_ids: null,
    outbound_schedule: null,
    timezone: null
  }

  // A form does not necessarily use all QNS fields
  // The loadedQnsSettings object holds the loaded (or default empty) QNS settings
  // When saving the QNS settings back, the form will update the touched fields from the adapters
  // The unused/untouched values will be still set in the save payload from this loadedQnsSettings
  let accountQnsSettings: Record<string, any> | null = null
  let isAccountQnsSettingsMissing = false
  // In non-trivial scenarios we may need to access the current state of the applied
  // settings. These variables keep track of the settings last applied with the apply() function
  // and can be queried with the getState() function
  let currentDbSettings: Record<string, Record<string, any>> = {}
  let currentQnsSettings: Record<string, any> = {}

  // TODO: until proper observable module usage is implemented we may need to track if the QNS settings are loaded
  //       and available to prevent the emptyQnsSettings being applied unexpectedly because a new instance of this form
  //       has never loaded the QNS data
  let shouldLoadBeforeSave = true

  const loadDbSettings = async (): Promise<Record<string, Record<string, any>>> => {
    const variables = adapters.getByType(SettingType.DB).map(a => a.variable)
    if (variables.length === 0) {
      return {}
    }
    const settingsList = await egdApiClient.accountService.getAccountSettings(accountId, variables)
    const settingsObj: Record<string, Record<string, any>> = {}
    settingsList.forEach(item => {
      settingsObj[item.variable] = item
    })
    return settingsObj
  }

  const fetchAccountQnsSettings = async () => {
    try {
      return await egdApiClient.accountService.getAccountQnsSettings(accountId)
    } catch (error) {
      if (error?.response?.status === 494) {
        isAccountQnsSettingsMissing = true
        return null
      }
      throw error
    }
  }

  const loadQnsSettings = async () => {
    const hasQns = adapters.getByType(SettingType.QNS).length > 0
    if (!hasQns) {
      return emptyQnsSettings
    }
    accountQnsSettings = await fetchAccountQnsSettings()
    return accountQnsSettings || emptyQnsSettings
  }

  const load = async (): Promise<Record<string, any>> => {
    const [dbSettings, qnsSettings] = await Promise.all([loadDbSettings(), loadQnsSettings()])
    shouldLoadBeforeSave = false
    return apply(dbSettings, qnsSettings)
  }

  const apply = (dbSettings: Record<string, Record<string, any>>, qnsSettings: Record<string, any>) => {
    currentDbSettings = dbSettings
    currentQnsSettings = qnsSettings
    const formData: Record<string, any> = {}

    adapters.getByType(SettingType.DB).forEach(adapter => {
      if (adapter.skipFormPopulateOnLoad) {
        return
      }

      const setting = dbSettings[adapter.variable]
      if (setting) {
        adapter.setRawValue(setting.value)
      }
      formData[adapter.inputName] = adapter.getUiValue()
    })

    adapters.getByType(SettingType.QNS).forEach(adapter => {
      if (adapter.skipFormPopulateOnLoad) {
        return
      }

      adapter.setRawValue(qnsSettings[adapter.variable])
      formData[adapter.inputName] = adapter.getUiValue()
    })

    return formData
  }

  const saveDbSettings = async (dbSettings: Array<Record<string, any>>) =>
    egdApiClient.accountService.saveAccountSettings(accountId, dbSettings)

  const saveQnsSettings = async (qnsSettings: Record<string, any>) =>
    egdApiClient.accountService.saveAccountQnsSettings(accountId, qnsSettings)

  const save = async (dirtyFormData: Partial<Record<string, any>>) => {
    if (shouldLoadBeforeSave) {
      // eslint-disable-next-line no-console
      console.warn('It appears the settings have not yet been loaded in the form instance. Triggering a load.')
      await load()
    }
    const updates: Promise<any>[] = []

    const dbSettings = formDataToDbSettings(dirtyFormData)
    if (dbSettings.length > 0) {
      updates.push(saveDbSettings(dbSettings))
    }

    const qnsSettings = formDataToQnsSettings(dirtyFormData)
    if (qnsSettings) {
      const updateQnsSettings = { ...(accountQnsSettings || emptyQnsSettings), ...qnsSettings }
      updates.push(saveQnsSettings(updateQnsSettings))
    }
    await Promise.all(updates)
    await load()
  }

  const formDataToDbSettings = (dirtyFormData: Partial<Record<string, any>>): Array<Record<string, any>> => {
    const dbSettings: Array<Record<string, any>> = []
    Object.entries(dirtyFormData).forEach(([fieldName, fieldValue]) => {
      const adaptersByInputName = adapters.getByInputName(fieldName)
      if (adaptersByInputName.length === 0) {
        return
      }
      adaptersByInputName.forEach(adapter => {
        if (!adaptersByInputName || adapter.type !== SettingType.DB) {
          return
        }
        adapter.setUiValue(fieldValue)
        dbSettings.push({ variable: adapter.variable, value: adapter.getRawValue() })
      })
    })
    return dbSettings
  }

  const formDataToQnsSettings = (dirtyFormData: Partial<Record<string, any>>): Record<string, any> | null => {
    let isQnsSettingsChange = false
    const qnsSettings: Record<string, any> = {}
    const dirtyFieldNames = Object.keys(dirtyFormData)
    adapters.getByType(SettingType.QNS).forEach(adapter => {
      if (dirtyFieldNames.includes(adapter.inputName)) {
        adapter.setUiValue(dirtyFormData[adapter.inputName])
        qnsSettings[adapter.variable] = adapter.getRawValue()
        isQnsSettingsChange = true
      }
    })
    if (!isQnsSettingsChange) {
      return null
    }
    return qnsSettings
  }

  const getInitialValues = (): Record<string, any> => adapters.getInitialValues()

  const getState = () => ({
    accountQnsSettings,
    isAccountQnsSettingsMissing,
    currentDbSettings,
    currentQnsSettings
  })

  const isReadonly = auth.isReadonlyAccountSettings(accountId)

  const canRead = auth.canReadAccountSettings(accountId)

  return Object.freeze({
    load,
    save,
    getInitialValues,
    isAccountQnsSettingsMissing,
    getState,
    apply,
    isReadonly,
    canRead,
    adapters
  })
}
